import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, ObservableInput, Subject, throwError as observableThrowError } from 'rxjs';
import { catchError, mergeMap, switchMap } from "rxjs/operators";
import { Utilities } from '../models/utilities.model';
import { AlertService, MessageSeverity } from './alert.service';
import { AuthService } from './auth.service';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class EndpointFactoryService {

  static readonly apiVersion: string = "1";

  private readonly _loginTokenUrl: string = "/api/Core/Authorization/token";

  private get loginUrl() { return new Utilities().baseUrl + this._loginTokenUrl; }

  private taskPauser: Subject<any>;
  private isRefreshingLogin: boolean;

  constructor(protected http: HttpClient, protected alertService: AlertService,
    private authService: AuthService) { }

  /**
 * Call http get with authorization and map result to specified type
 * @param url - the URL to call
 * @returns Result of get request mapped to TResult type
 */
  protected getAuthorized<TData>(url: string, params?: HttpParams): Observable<TData> {
    return this.http.get<TData>(url, { headers: this.getAuthHeaders(), params: params });
  }

  /**
   * Call http get with authorization and map result to specified type
   * @param url - the URL to call
   * @param continuation - the method to call
   * @returns Result of get request mapped to TResult type
   */
  protected getAuthorizedWithErrorHandling<TData>(url: string, continuation: () => Observable<TData>, params?: HttpParams, showError: boolean = true): Observable<TData> {
    return this.http.get<TData>(url, { headers: this.getAuthHeaders(), params: params })
      .pipe(catchError(error => this.handleError<TData>(error, continuation, showError)));
  }


  /**
  * Call http get with authorization and response type as string (no json)
  * @param url - the URL to call
  * @param continuation - the method to call
  * @returns Result of get request mapped to TResult type
  */
  protected getAuthorizedWithErrorHandlingAndNoJsonResult<TData>(url: string, continuation: () => Observable<TData>, params?: HttpParams): Observable<TData> {
    return this.http.get<TData>(url, { headers: this.getAuthHeaders(false), responseType: 'text' as 'json', params: params })
      .pipe(catchError(error => this.handleError<TData>(error, continuation)));
  }

  /**
   * Call http put with authorization and map parameter and result to specified type
   * @param {string} url - the URL to call (including item ID)
   * @param {TData} data - the data to put
   * @returns - response data from server
   */
  protected putAuthorized<TData>(url: string, data: any): Observable<TData> {
    return this.http.put<TData>(url, data, { headers: this.getAuthHeaders() });
  }

  /**
   * Call http put with authorization and map parameter and result to specified type
   * @param {string} url - the URL to call (including item ID)
   * @param {TData} data - the data to put
   * @param continuation - the method to call
   * @returns - response data from server
   */
  protected putAuthorizedWithErrorHandling<TData>(url: string, data: any, continuation: () => Observable<TData>, params?: HttpParams, showError: boolean = true): Observable<TData> {
    return this.http.put<TData>(url, data, { headers: this.getAuthHeaders(), params: params })
      .pipe(catchError(error => this.handleError<TData>(error, continuation, showError)));
  }

  /**
  * Call http delete with authorization
  * @param {string} url - the URL to call (including ID of item to delete)
  * @returns Response from server
  */
  protected deleteAuthorizedWithErrorHandling<TData>(url: string, continuation: () => Observable<TData>, params?: HttpParams, showError: boolean = true): Observable<any> {
    return this.http.delete(url, { headers: this.getAuthHeaders(), params: params })
      .pipe(catchError<TData, ObservableInput<TData>>(error => this.handleError<TData>(error, continuation, showError)));
  }

  /**
   * Call http post with authorization
   * @param {string} url - the URL to call (including ID of item to delete)
   * @param {TData} data - the data to put
   * @returns Response from server
   */
  protected postAuthorized<TData>(url: string, data: any): Observable<TData> {
    return this.http.post<TData>(url, data, { headers: this.getAuthHeaders() });
  }

  /**
   * Call http post with authorization
   * @param {string} url - the URL to call (including ID of item to delete)
   * @param {TData} data - the data to put
   * @param continuation - the method to call
   * @returns Response from server
   */
  protected postAuthorizedWithErrorHandling<TData>(url: string, data: any | null, continuation: () => Observable<TData>, params?: HttpParams, showError: boolean = true): Observable<TData> {
    return this.http.post<TData>(url, data, { headers: this.getAuthHeaders(), params: params })
      .pipe(catchError(error => this.handleError<TData>(error, continuation, showError)));
  }

  protected handleError<T>(error: HttpErrorResponse, continuation: () => Observable<T>, showError: boolean = true): Observable<T> {

    switch (error.status) {
      case 401: {
        if (this.isRefreshingLogin) {
          return this.pauseTask<T>(continuation);
        }

        if (this.authService.isLoggedIn) {
          this.isRefreshingLogin = true;

          return this.authService.refreshLogin(continuation)
            .pipe(mergeMap(data => {
              this.isRefreshingLogin = false;
              this.resumeTasks(true);
              return continuation();
            }),
              catchError(refreshLoginError => {
                this.isRefreshingLogin = false;
                this.resumeTasks(false);
                if (refreshLoginError.status == 401 || (refreshLoginError.url && refreshLoginError.url.toLowerCase().includes(this.loginUrl.toLowerCase()))) {
                  this.authService.reLogin();
                  this.showMessageSessionExpired();
                  //console.log("session expired => 401 Fehler => refresh Login schlägt fehl #####");
                  return observableThrowError('session expired');
                } else {
                  this.showMessageServerError;
                  return observableThrowError(refreshLoginError || 'server error');
                }
              }));

        } else {
          return observableThrowError(error || 'server error');
        }
      }
      case 403: {
        if (showError) {
          this.showMessageNotAllowed();
        }
        return observableThrowError(error || 'server error');
      }
      case 404: {
        if (showError) {
          this.showMessageDataNotFound();
        }
        return observableThrowError(error || 'server error');
      }
      case 400: {
        //this.showMessageInvalidRecord(error.error)
        return observableThrowError(error || 'server error');
      }
    }

    if (error.url && error.url.toLowerCase().includes(this.loginUrl.toLowerCase())) {
      this.authService.reLogin();
      this.showMessageSessionExpired();
      return observableThrowError('session expired');
    }
    else {
      this.showMessageServerError;
      return observableThrowError(error || 'server error');
    }
  }

  private showMessageInvalidRecord(error: any) {
    this.alertService.showMessage('Serverfehler', 'Ungültiger Datensatz: ' + error, MessageSeverity.error);
  }

  private showMessageSessionExpired() {
    this.alertService.showMessage("", 'Die Sitzung ist abgelaufen!', MessageSeverity.info);
  }

  private showMessageNotAllowed() {
    this.alertService.showMessage("", 'Sie sind nicht berechtigt!', MessageSeverity.warn);
  }

  private showMessageDataNotFound() {
    this.alertService.showMessage("", 'Der Datensatz konnte nicht gefunden werden!', MessageSeverity.warn);
  }

  private showMessageServerError() {
    this.alertService.showMessage('Serverfehler', "", MessageSeverity.error);
  }

  private pauseTask<T>(continuation: () => Observable<T>): Observable<T> {
    if (!this.taskPauser)
      this.taskPauser = new Subject();

    return this.taskPauser.pipe(switchMap(continueOp => {
      return continueOp ? continuation() : observableThrowError('session expired');
    }));
  }


  private resumeTasks(continueOp: boolean) {
    setTimeout(() => {
      if (this.taskPauser) {
        this.taskPauser.next(continueOp);
        this.taskPauser.complete();
        this.taskPauser = null;
      }
    });
  }

  public getAuthHeaders(includeJsonContentType?: boolean): HttpHeaders {
    let headers = new HttpHeaders({ 'Authorization': 'Bearer ' + this.authService.accessToken });

    if (includeJsonContentType)
      headers.append("Content-Type", "application/json");


    headers.append("Accept", `application/vnd.iman.v${EndpointFactoryService.apiVersion}+json, application/json, text/plain, */*`);

    return headers;
  }

  /**
   * Convert date part of Date to string for server request
   * @param date - the date to convert
   * @returns string containig the date part only
   */
  protected dateString(date: Date): string {
    return moment(date).format("Y-MM-DD");
  }

  /**
   * Convert date and time of Date to string for server request
   * @param date - the date and time to convert
   * @returns string containig the date and time without time zone or UTC mark
   */
  protected dateTimeString(date: Date): string {
    return moment(date).format("Y-MM-DD[T]HH:mm:ss");
  }

}
